import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, CreditCard, Star, Crown, Headphones, Car, DollarSign, Bell, ShieldCheck, Building2, Rocket, Wallet, Settings, Calendar, ClipboardList, Handshake } from "lucide-react";

/**
 * /help — In-app walkthrough for customers and drivers.
 * Markdown source-of-truth lives at /app/memory/{CUSTOMER,DRIVER}_GUIDE.md.
 * This file mirrors that content as styled JSX for in-app reading.
 */

function Section({ icon: Icon, title, children, testId }) {
  return (
    <Card className="bg-[#121212] border-white/5" data-testid={testId}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0047FF]/15 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#4D88FF]" />
          </div>
          <h3 className="text-base font-semibold text-white">{title}</h3>
        </div>
        <div className="text-sm text-zinc-300 leading-relaxed space-y-2 pl-1">{children}</div>
      </CardContent>
    </Card>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0047FF] text-white text-xs font-bold flex items-center justify-center">{n}</div>
      <div className="flex-1 text-sm text-zinc-300">{children}</div>
    </div>
  );
}

function CustomerGuide() {
  return (
    <div className="space-y-4">
      <Section icon={MapPin} title="1. Booking a Ride or Delivery" testId="help-customer-booking">
        <Step n="1">Open the app. Pick <strong>Standard Ride</strong>, <strong>XL</strong>, or <strong>Same-day Delivery</strong>.</Step>
        <Step n="2">Tap <strong>Pickup</strong> and type the address — local results show first, no state/zip needed.</Step>
        <Step n="3">Add <strong>Dropoff</strong>. Tap <strong>+ Add Stop</strong> for up to 5 stops.</Step>
        <Step n="4">Choose <strong>Now</strong> or <strong>Schedule for later</strong>. Review the quote.</Step>
        <Step n="5">Tap <strong>Continue</strong> to check out.</Step>
      </Section>

      <Section icon={CreditCard} title="2. Paying for Your Trip" testId="help-customer-pay">
        <p><strong className="text-white">Stripe (Card)</strong> — pay online with credit/debit.</p>
        <p><strong className="text-white">JIM</strong> — driver taps your card on a phone reader at pickup.</p>
        <p><strong className="text-white">Cash</strong> — pay the driver directly, exact change preferred.</p>
        <p className="text-xs text-zinc-500 mt-2">Tips are <strong>untaxed</strong> and go 100% to the driver.</p>
      </Section>

      <Section icon={Crown} title="3. Saving with Link Pro" testId="help-customer-linkpro">
        <Step n="1">Tap the <strong>Link Pro</strong> button in the header.</Step>
        <Step n="2">Pick <strong>Weekly · Monthly · Yearly</strong>.</Step>
        <Step n="3">Check out via Stripe. You're billed automatically.</Step>
        <p className="text-xs text-zinc-500 mt-2">Members earn <strong>1.5× loyalty points</strong> on every trip and unlock lower base fares.</p>
      </Section>

      <Section icon={Star} title="4. ⭐ Redeeming Loyalty Points (READ THIS!)" testId="help-customer-points">
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 mb-2">
          <p className="text-amber-200 text-sm font-semibold">Points do NOT auto-apply at checkout.</p>
          <p className="text-amber-200/80 text-xs mt-1">You must redeem them into <strong>spendable cash credit</strong> first.</p>
        </div>
        <Step n="1">Tap your avatar → <strong>Account</strong>.</Step>
        <Step n="2">Open the <strong>Loyalty / Points</strong> tab.</Step>
        <Step n="3">Tap <strong>Redeem Points → Cash Credit</strong>.</Step>
        <Step n="4">Go back to <strong>Book a Ride</strong>. The credit auto-applies at checkout.</Step>
        <p className="text-xs text-zinc-500 mt-2">New customers: tap the yellow <strong>Welcome gift</strong> banner for $5 off your first ride.</p>
      </Section>

      <Section icon={MapPin} title="5. Live-Tracking Your Driver" testId="help-customer-track">
        <p>Once a driver claims your trip, watch their live location, tap <strong>Call</strong> or <strong>Message</strong> for in-app chat, and see live ETA updates.</p>
        <p className="text-xs text-zinc-500">Re-open any trip anytime from <strong>Account → My Trips</strong>.</p>
      </Section>

      <Section icon={ShieldCheck} title="6. Resetting Your Password" testId="help-customer-reset">
        <Step n="1">Tap <strong>Sign in</strong> → <strong>Forgot password?</strong></Step>
        <Step n="2">Enter your email. Check inbox (or spam) for the reset link.</Step>
        <Step n="3">Set your new password and log in.</Step>
      </Section>

      <Section icon={Headphones} title="7. Getting Help" testId="help-customer-support">
        <p>📞 Tap <strong>Need Help?</strong> in the header for the support phone.</p>
        <p>✉️ link@linklitepro.com</p>
        <p className="text-xs text-zinc-500 mt-2">Allow GPS for the most accurate pickup suggestions. If denied, results still bias to your local area.</p>
      </Section>
    </div>
  );
}

function DriverGuide() {
  return (
    <div className="space-y-4">
      <Section icon={ShieldCheck} title="1. Getting Approved" testId="help-driver-approval">
        <Step n="1">Tap <strong>Become a Driver</strong> on the home screen.</Step>
        <Step n="2">Fill in basic info — vehicle year must be ≥ 2016.</Step>
        <Step n="3">Upload Driver's License, Insurance (rideshare endorsement), Vehicle Registration.</Step>
        <Step n="4">Submit and wait for admin approval (you'll be emailed).</Step>
        <Step n="5">Once approved, jobs appear at <strong>/driver/bookings</strong>.</Step>
      </Section>

      <Section icon={Car} title="2. Claiming Jobs" testId="help-driver-claim">
        <p>Three dispatch modes (set by admin):</p>
        <p><strong className="text-white">Manual</strong> — first to tap <strong>Claim</strong> wins.</p>
        <p><strong className="text-white">Auto · by time</strong> — offer rolls every 30 seconds.</p>
        <p><strong className="text-white">Auto · by distance</strong> — closest driver gets it first.</p>
        <p className="text-xs text-zinc-500 mt-2">If a job shows "Link Admin assigned," tap <strong>Claim trips</strong> at the top of your jobs list to sweep it.</p>
      </Section>

      <Section icon={MapPin} title="3. Starting a Trip" testId="help-driver-start">
        <Step n="1">Open the job from <strong>My Jobs</strong>.</Step>
        <Step n="2">Tap <strong>Start Trip</strong> at pickup.</Step>
        <Step n="3">Live map opens — your GPS broadcasts to the customer.</Step>
        <Step n="4">Tap <strong>Navigate</strong> for Google/Apple/Waze directions.</Step>
        <Step n="5">Use <strong>Message</strong> / <strong>Call</strong> for customer contact.</Step>
      </Section>

      <Section icon={DollarSign} title="4. Ending a Trip & Getting Paid" testId="help-driver-end">
        <p>Tap <strong>End Trip</strong> after dropoff. Stripe finalizes automatically. For Cash/JIM, confirm receipt before marking complete.</p>
        <p className="text-xs text-zinc-500 mt-2">Tips entered by the customer are <strong>100% yours</strong>, untaxed, added directly to your payout.</p>
      </Section>

      <Section icon={DollarSign} title="5. Earnings Dashboard" testId="help-driver-earnings">
        <p>Tap <strong>Earnings</strong> in the bottom nav for Today / Week / Month / Year totals, per-trip breakdowns, and Stripe Connect Express setup for direct bank payouts.</p>
      </Section>

      <Section icon={Bell} title="6. Push Notifications (Important)" testId="help-driver-push">
        <p>You'll miss jobs if push is off. Enable:</p>
        <Step n="1">Avatar → <strong>Driver Settings</strong>.</Step>
        <Step n="2">Toggle <strong>Web Push Notifications</strong> ON, allow when prompted.</Step>
        <Step n="3">Tap <strong>Send test ping</strong> to confirm.</Step>
        <p className="text-xs text-zinc-500 mt-2">On iOS, you must <strong>install Link to your home screen</strong> first (Install Link in the menu).</p>
      </Section>

      <Section icon={ShieldCheck} title="7. Common Issues" testId="help-driver-issues">
        <p><strong className="text-white">Blank map after Start Trip:</strong> force-refresh the page, re-tap Start.</p>
        <p><strong className="text-white">Job won't claim:</strong> another driver got it first, or you're not approved yet.</p>
        <p><strong className="text-white">No push pings:</strong> re-enable from Driver Settings. iOS requires installed PWA.</p>
        <p><strong className="text-white">Stripe payout missing:</strong> finish Stripe Connect onboarding in Earnings tab.</p>
      </Section>
    </div>
  );
}

function FranchiseGuide() {
  return (
    <div className="space-y-4">
      {/* Highlight bar for the trial program so any new applicant sees it
          before diving into the general sections. */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="help-franchise-trial-callout">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Handshake className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-bold text-emerald-100 uppercase tracking-wider">30-day operator trial</p>
            <p className="text-sm text-emerald-50/90 leading-snug">
              First month is free while you set up your city — no software fee, no card required to apply.
              You keep 100% of every fare during the trial. Convert to a paid tenant any time by signing
              the fee schedule; suspend anytime with zero commitment.
            </p>
          </div>
        </div>
      </div>

      <Section icon={Building2} title="1. What Licensing Gets You" testId="help-franchise-overview">
        <p>You run Link Ride under your own brand in your city. We keep the tech running; you keep your drivers, your riders, and the majority of every fare.</p>
        <p><strong className="text-white">You get:</strong> your own tenant with custom name, colors, logo, subdomain, and support contact — plus the full app (customer booking, driver app, dispatch, live tracking, chat, Stripe Connect payouts).</p>
        <p><strong className="text-white">You control:</strong> local pricing, sales tax rate, service area, driver approvals, and your operating hours.</p>
      </Section>

      <Section icon={ClipboardList} title="2. What To Prepare Before Applying" testId="help-franchise-prep">
        <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1">Have these ready — they make provisioning same-day.</p>
        <Step n="1"><strong className="text-white">Legal business name + EIN</strong> (LLC / Corp / DBA — sole prop with EIN is fine).</Step>
        <Step n="2"><strong className="text-white">Commercial insurance status</strong> — active, in-progress, or a broker you're working with. We accept trial applications with a broker letter of intent.</Step>
        <Step n="3"><strong className="text-white">TNC / rideshare permit</strong> for your state (PA, NY, NJ, and many others require this). We can point you to the right filing.</Step>
        <Step n="4"><strong className="text-white">Bank account details</strong> for Stripe Connect Express — routing + account number. You add these directly to Stripe, we never see them.</Step>
        <Step n="5"><strong className="text-white">Rough service area + starting fleet size</strong> — a couple of ZIP codes + how many drivers you plan to sign up in month one.</Step>
        <Step n="6"><strong className="text-white">Brand assets (optional at apply):</strong> logo (SVG or 512×512 PNG), 1-2 brand colors (hex). You can upload these after approval too.</Step>
      </Section>

      <Section icon={Rocket} title="3. How to Apply" testId="help-franchise-apply">
        <Step n="1">Tap <strong>Franchise</strong> in the header or open <strong>/franchise</strong>.</Step>
        <Step n="2">Fill in the operator application — business name, city, state, contact info, expected drivers.</Step>
        <Step n="3">Answer the <strong>qualifying compliance section</strong> — EIN, insurance status, TNC permit status, responsible party.</Step>
        <Step n="4">Review + sign the licensing docs (SaaS License, Master License, Privacy, DPA, AUP) directly in the flow.</Step>
        <Step n="5">Submit. You'll get an email confirmation with a copy of your signed paperwork attached.</Step>
        <Step n="6">We review within <strong>1-2 business days</strong> and email you next steps.</Step>
      </Section>

      <Section icon={Calendar} title="4. Your First 30 Days — Onboarding Timeline" testId="help-franchise-timeline">
        <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1">What happens after approval.</p>
        <div className="space-y-3 mt-2">
          <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
            <p className="text-sm font-bold text-[#4D88FF]">Day 0 — Approval</p>
            <p className="text-xs text-zinc-400 mt-0.5">You get an approval email with your tenant slug, admin login, and a link to a 30-minute onboarding call.</p>
          </div>
          <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
            <p className="text-sm font-bold text-[#4D88FF]">Days 1-3 — Configure</p>
            <p className="text-xs text-zinc-400 mt-0.5">Upload brand + logo, set pricing / sales tax, define service area, connect Stripe Express, wire your support email.</p>
          </div>
          <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
            <p className="text-sm font-bold text-[#4D88FF]">Days 4-7 — Invite drivers</p>
            <p className="text-xs text-zinc-400 mt-0.5">Send driver signup links, approve documents, background checks, verify vehicle year ≥ 2016.</p>
          </div>
          <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
            <p className="text-sm font-bold text-[#4D88FF]">Days 8-14 — Soft launch</p>
            <p className="text-xs text-zinc-400 mt-0.5">Open to friends + family bookings. Confirm every screen shows your brand, not ours. Test cash, JIM, and Stripe.</p>
          </div>
          <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
            <p className="text-sm font-bold text-[#4D88FF]">Days 15-30 — Public launch</p>
            <p className="text-xs text-zinc-400 mt-0.5">Flip your tenant status to <code>active</code>, start local marketing. We hop on a 15-min check-in around day 28 to review your trial data + convert to paid.</p>
          </div>
        </div>
      </Section>

      <Section icon={Settings} title="5. Getting Your Tenant Live" testId="help-franchise-setup">
        <Step n="1">Once approved, we provision your tenant slug (e.g. <code>readingpa</code>) and admin login.</Step>
        <Step n="2">Log in and open <strong>Admin → Franchise Settings</strong>: upload your logo, pick brand colors, set your support phone / email.</Step>
        <Step n="3">Set <strong>pricing</strong> — base fare, per-mile, minimum, package rates — and your <strong>local sales tax</strong> percent.</Step>
        <Step n="4">Configure your <strong>dispatch mode</strong> (manual / auto-by-time / auto-by-distance).</Step>
        <Step n="5">Invite drivers, approve their documents, and open for bookings.</Step>
        <p className="text-xs text-zinc-500 mt-2">Your custom subdomain (e.g. <code>readingpa.link</code>) points at your tenant so riders and drivers see your brand — not ours.</p>
      </Section>

      <Section icon={Wallet} title="6. Payouts & Fees" testId="help-franchise-payouts">
        <p><strong className="text-white">During your 30-day trial:</strong> zero platform fee. 100% of every fare stays with you (minus card fees Stripe takes).</p>
        <p><strong className="text-white">After trial:</strong> a flat monthly software fee plus a small per-trip fee — locked in your signed License Agreement. Everything else (driver payouts, tips, cash, JIM) flows straight to you.</p>
        <p><strong className="text-white">Stripe Connect Express</strong> — connect your own bank account. Rider payments land in your Stripe balance, minus card fees and the platform fee.</p>
        <p><strong className="text-white">Driver payouts</strong> are dispatched from YOUR Stripe balance to each driver's Connect account on the standard schedule.</p>
        <p className="text-xs text-zinc-500 mt-2">Full fee schedule is on your signed License Agreement (Admin → Paperwork → SaaS License).</p>
      </Section>

      <Section icon={ShieldCheck} title="7. Managing Your Operation" testId="help-franchise-admin">
        <p>Your <strong>Admin Dashboard</strong> gives you:</p>
        <p>• Booking table with reassign, edit, refund, and no-show tools</p>
        <p>• Driver approval queue with document review + background check status</p>
        <p>• Live dispatch map showing every active driver in your city</p>
        <p>• Tax + reporting tab (sales tax collected, driver 1099 CSV/PDF, business tax packet)</p>
        <p>• Customer support inbox (in-app chat + email routing to your address)</p>
        <p>• Franchise Settings tab for logo, brand colors, subdomain, support contact</p>
      </Section>

      <Section icon={Headphones} title="8. Getting Help" testId="help-franchise-support">
        <p>📧 Operators email: <a href="mailto:link@linklitepro.com" className="text-[#4D88FF] hover:underline">link@linklitepro.com</a></p>
        <p>📞 Direct line to platform ops for approved operators (in your welcome email).</p>
        <p>📄 Signed paperwork + PDFs always available at <strong>Admin → Paperwork</strong>.</p>
        <p className="text-xs text-zinc-500 mt-2">Every legal document is a live PDF — regenerate with your current business name + effective date any time.</p>
      </Section>
    </div>
  );
}

export default function Help() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const initialTab = roleParam === 'driver' ? 'driver'
    : roleParam === 'franchise' ? 'franchise'
    : 'customer';
  const [tab, setTab] = useState(initialTab);

  const tabLabel = tab === 'customer' ? 'Customer'
    : tab === 'driver' ? 'Driver'
    : 'Franchise';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-12">
      <header className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
          data-testid="help-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-base sm:text-lg font-bold">How Link Ride Works</h1>
          <p className="text-[11px] text-zinc-500">Quick walkthrough · {tabLabel} guide</p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-900 border border-white/5 w-full grid grid-cols-3 mb-5">
            <TabsTrigger value="customer" data-testid="help-tab-customer">For Customers</TabsTrigger>
            <TabsTrigger value="driver" data-testid="help-tab-driver">For Drivers</TabsTrigger>
            <TabsTrigger value="franchise" data-testid="help-tab-franchise">For Franchisees</TabsTrigger>
          </TabsList>
          <TabsContent value="customer"><CustomerGuide /></TabsContent>
          <TabsContent value="driver"><DriverGuide /></TabsContent>
          <TabsContent value="franchise"><FranchiseGuide /></TabsContent>
        </Tabs>

        <p className="text-center text-[11px] text-zinc-600 mt-8">
          Need more help? Email <a href="mailto:link@linklitepro.com" className="text-[#4D88FF] hover:underline">link@linklitepro.com</a>
        </p>
      </div>
    </div>
  );
}
